import React, { useState } from 'react';
import { eventsService } from '../../services/eventsService';
import './MovePositionWidget.css';

const MovePositionWidget = ({ className = '' }) => {
  const [form, setForm] = useState({
    sourcePosition: '',
    targetEvent: '',
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const sourcePosition = form.sourcePosition.trim();
    const targetEvent = form.targetEvent.trim();
    setMessage('');
    setError('');

    if (!sourcePosition || !targetEvent) {
      setError('Enter both the source position and target event.');
      return;
    }

    setSaving(true);
    const result = await eventsService.previewMovePosition({
      source_position: sourcePosition,
      target_event: targetEvent,
    });
    setSaving(false);

    if (!result.success) {
      setError(result.error);
      return;
    }

    setPreview(result);
  };

  const closePreview = () => {
    setPreview(null);
  };

  const confirmMove = async () => {
    const sourcePosition = form.sourcePosition.trim();
    const targetEvent = form.targetEvent.trim();

    if (!sourcePosition || !targetEvent) {
      setError('Enter both the source position and target event.');
      setPreview(null);
      return;
    }

    setSaving(true);
    setError('');
    setMessage('');
    const result = await eventsService.movePosition({
      source_position: sourcePosition,
      target_event: targetEvent,
    });
    setSaving(false);

    if (!result.success) {
      setError(result.error);
      setPreview(null);
      return;
    }

    setMessage(
      `${result.message} Units moved: ${result.units_moved}. Orders moved: ${result.orders_moved}.`,
    );
    setForm({
      sourcePosition: '',
      targetEvent: '',
    });
    setPreview(null);
  };

  return (
    <>
      <section className={`move-position-widget ${className}`.trim()}>
        <div className="move-position-widget-header">
          <h3>Move Position to Event</h3>
          <p className="move-position-widget-help">
            Move one position and its units to a different event group. Source accepts a position
            source UUID or event ID. Target accepts an event ref, event ID, or a source UUID from
            the target event group.
          </p>
        </div>

        <form className="move-position-widget-form" onSubmit={handleSubmit}>
          <label>
            <span>Source Position</span>
            <input
              type="text"
              value={form.sourcePosition}
              onChange={(event) => setForm((current) => ({
                ...current,
                sourcePosition: event.target.value,
              }))}
              placeholder="e.g. 10887a or event_id"
              disabled={saving}
            />
          </label>

          <label>
            <span>Target Event</span>
            <input
              type="text"
              value={form.targetEvent}
              onChange={(event) => setForm((current) => ({
                ...current,
                targetEvent: event.target.value,
              }))}
              placeholder="e.g. 2, 10940a, or event_id"
              disabled={saving}
            />
          </label>

          {error && (
            <div className="move-position-widget-alert move-position-widget-alert-error">
              {error}
            </div>
          )}

          {message && (
            <div className="move-position-widget-alert move-position-widget-alert-success">
              {message}
            </div>
          )}

          <button className="btn btn-primary move-position-widget-submit" type="submit" disabled={saving}>
            {saving ? 'Moving...' : 'Move Position'}
          </button>
        </form>
      </section>

      {preview && (
        <div className="move-position-preview-backdrop" onMouseDown={closePreview}>
          <section className="move-position-preview-modal" onMouseDown={(event) => event.stopPropagation()}>
            <div className="move-position-preview-header">
              <div>
                <h2>Confirm Position Move</h2>
                <p>
                  {preview.old_source_uuid} to {preview.proposed_source_uuid}
                </p>
              </div>
              <button
                className="move-position-preview-close"
                type="button"
                onClick={closePreview}
                disabled={saving}
              >
                x
              </button>
            </div>

            <div className="move-position-preview-grid">
              <article className="move-position-preview-card">
                <span className="move-position-preview-label">Source Position</span>
                <h3>{preview.source_item.event_name || '-'}</h3>
                <p>{preview.source_item.artist || '-'}</p>
                <p>{preview.source_item.event_date || '-'}</p>
                <p>{preview.source_item.venue || '-'}</p>
                <p>{preview.source_item.location || '-'}</p>
                <p>{preview.source_item.vendor || preview.source_item.platform || '-'}</p>
                <strong>{preview.old_source_uuid}</strong>
              </article>

              <article className="move-position-preview-card">
                <span className="move-position-preview-label">Target Event</span>
                <h3>{preview.target_item.event_name || '-'}</h3>
                <p>{preview.target_item.artist || '-'}</p>
                <p>{preview.target_item.event_date || '-'}</p>
                <p>{preview.target_item.venue || '-'}</p>
                <p>{preview.target_item.location || '-'}</p>
                <p>{preview.target_item.vendor || preview.target_item.platform || '-'}</p>
                <strong>{preview.new_event_ref} -&gt; {preview.proposed_source_uuid}</strong>
              </article>
            </div>

            <div className="move-position-preview-actions">
              <button className="btn btn-secondary" type="button" onClick={closePreview} disabled={saving}>
                Cancel
              </button>
              <button className="btn btn-primary" type="button" onClick={confirmMove} disabled={saving}>
                {saving ? 'Moving...' : 'Confirm Move'}
              </button>
            </div>
          </section>
        </div>
      )}
    </>
  );
};

export default MovePositionWidget;
